import type { MeResponse, Role, SessionResponse } from '@bingo/shared';
import { hasWebLocks, withLock } from './lock.js';

/**
 * Sessão do operador.
 *
 * O **access token vive só em memória** — nunca em `localStorage` (decisão 6):
 * `localStorage` é legível por qualquer script da origem, e um token de 15
 * minutos ali é 15 minutos de conta aberta para qualquer XSS. O refresh mora
 * num cookie `httpOnly` que o JavaScript não alcança, e é o navegador que o
 * manda nas rotas de `/api/auth`.
 *
 * ## Refresh coordenado entre abas
 *
 * /controle e /admin abertos no mesmo navegador compartilham o cookie. Sem
 * coordenação, as duas renovam juntas quando o access expira e a segunda chega
 * com o token recém-rotacionado. O servidor tem uma janela de tolerância de 30s
 * para isso (R1), mas ela é **rede de segurança, não plano**: a renovação passa
 * por `withLock`, que usa a Web Locks API quando ela existe e cai numa eleição
 * por `BroadcastChannel` quando não — o que é o caso em **http na rede local do
 * salão**, onde `navigator.locks` é `undefined` por não ser contexto seguro.
 */

interface SessionState {
  accessToken: string | null;
  expiresAt: number;
  user: SessionResponse['user'] | null;
  memberships: SessionResponse['memberships'];
  activeOrganizationId: string | null;
  activeRole: Role | null;
}

/** O que a UI enxerga. Sem o token: nenhuma tela precisa dele. */
export interface SessionSnapshot {
  user: SessionResponse['user'] | null;
  memberships: SessionResponse['memberships'];
  activeOrganizationId: string | null;
  activeRole: Role | null;
  signedIn: boolean;
  /**
   * A tentativa de retomar a sessão pelo cookie já terminou?
   *
   * `signedIn: false` com `ready: false` significa "ainda não sabemos", e é
   * diferente de "não está logado". Sem essa distinção, toda recarga de
   * página mandava o operador de volta para o /entrar no primeiro quadro —
   * mesmo com o cookie de refresh válido por 30 dias — porque a decisão era
   * tomada antes de alguém perguntar ao servidor.
   */
  ready: boolean;
}

const state: SessionState = {
  accessToken: null,
  expiresAt: 0,
  user: null,
  memberships: [],
  activeOrganizationId: null,
  activeRole: null,
};

/** Vira `true` quando a primeira tentativa de retomada termina (deu certo ou não). */
let bootstrapped = false;

/**
 * Instantâneo IMUTÁVEL do estado.
 *
 * Isto não é preciosismo: `state` é um objeto mutável único, e publicá-lo
 * direto fazia `setState(state)` receber sempre a MESMA referência. O React
 * compara com `Object.is` e descarta a atualização — o login funcionava, o
 * estado mudava, e a tela nunca re-renderizava. Era o bug do /admin preso no
 * formulário depois de um login bem-sucedido.
 *
 * O /parear escapava por acaso, porque mapeava para um booleano
 * (`false → true` é mudança de verdade).
 *
 * Cada publicação cria um objeto novo, e `sessionSnapshot()` devolve sempre o
 * mesmo até a próxima mudança — que é exatamente o contrato que
 * `useSyncExternalStore` exige.
 */
function buildSnapshot(): SessionSnapshot {
  return Object.freeze({
    user: state.user,
    memberships: state.memberships,
    activeOrganizationId: state.activeOrganizationId,
    activeRole: state.activeRole,
    signedIn: Boolean(state.user),
    ready: bootstrapped,
  });
}

let snapshot: SessionSnapshot = buildSnapshot();

/** A organização ativa é preferência de UI; o servidor sempre reconfere. */
const ORG_STORAGE_KEY = 'bingo.activeOrganizationId';
const REFRESH_LOCK = 'bingo.refresh';
const CHANNEL_NAME = 'bingo.session';

type Listener = (snapshot: SessionSnapshot) => void;
const listeners = new Set<Listener>();

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);

/** Renova um pouco antes de expirar, para a requisição não pegar a virada. */
const RENEW_MARGIN_MS = 30_000;

function notify() {
  snapshot = buildSnapshot();
  for (const listener of listeners) listener(snapshot);
}

/**
 * Assina mudanças de sessão.
 *
 * NÃO chama o listener na inscrição — quem precisa do valor inicial usa
 * `sessionSnapshot()`. É o contrato esperado por `useSyncExternalStore`.
 */
export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function sessionSnapshot(): SessionSnapshot {
  return snapshot;
}

export function storedOrganizationId(): string | null {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function applySession(session: SessionResponse, broadcast = true) {
  bootstrapped = true;
  state.accessToken = session.accessToken;
  state.expiresAt = Date.now() + session.expiresIn * 1000;
  state.user = session.user;
  state.memberships = session.memberships;
  state.activeOrganizationId = session.activeOrganizationId;
  state.activeRole = session.activeRole;

  try {
    if (session.activeOrganizationId) {
      localStorage.setItem(ORG_STORAGE_KEY, session.activeOrganizationId);
    }
  } catch {
    // Navegador em modo privado: a preferência simplesmente não persiste.
  }

  notify();

  // As outras abas não recebem o token (ele é de memória, por aba), mas
  // precisam saber que a sessão mudou para renovar o próprio.
  if (broadcast) channel?.postMessage({ type: 'session-updated' });
}

/**
 * Adota uma sessão emitida por outro caminho que não o login.
 *
 * Convite aceito e senha redefinida respondem com a mesma `SessionResponse`
 * do login — a pessoa já está autenticada quando a tela termina. Sem isto,
 * ela cairia no formulário de entrada logo depois de criar a senha, para
 * digitar o que acabou de escolher.
 */
export function adoptSession(session: SessionResponse): void {
  applySession(session);
}

export function applyMe(me: MeResponse) {
  state.user = me.user;
  state.memberships = me.memberships;
  state.activeOrganizationId = me.activeOrganizationId;
  state.activeRole = me.activeRole;
  notify();
}

export function clearSession(broadcast = true) {
  bootstrapped = true;
  state.accessToken = null;
  state.expiresAt = 0;
  state.user = null;
  state.memberships = [];
  state.activeOrganizationId = null;
  state.activeRole = null;
  notify();

  if (broadcast) channel?.postMessage({ type: 'session-cleared' });
}

channel?.addEventListener('message', (event: MessageEvent) => {
  const data = event.data as { type?: string } | null;
  if (data?.type === 'session-cleared') clearSession(false);
  // `session-updated` de outra aba invalida o token DESTA aba: ela renova
  // sozinha na próxima requisição, pelo mesmo lock.
  if (data?.type === 'session-updated') state.expiresAt = 0;
});

async function callRefresh(organizationId: string | null): Promise<SessionResponse | null> {
  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(organizationId ? { organizationId } : {}),
  });

  if (!response.ok) return null;
  return (await response.json()) as SessionResponse;
}

/** Renovação serializada: só uma aba, e só uma chamada por aba. */
let inFlight: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  const organizationId = state.activeOrganizationId ?? storedOrganizationId();

  const run = async (): Promise<string | null> => {
    // Outra aba pode ter renovado enquanto esperávamos o lock.
    if (state.accessToken && Date.now() < state.expiresAt - RENEW_MARGIN_MS) {
      return state.accessToken;
    }

    const session = await callRefresh(organizationId);
    if (!session) {
      clearSession();
      return null;
    }

    applySession(session);
    return session.accessToken;
  };

  // `withLock` escolhe sozinho entre Web Locks e a eleição por
  // BroadcastChannel; nenhum dos dois exige HTTPS no caminho de fallback.
  return withLock(REFRESH_LOCK, run);
}

/** Token válido, renovando se necessário. `null` = precisa entrar de novo. */
export async function accessToken(): Promise<string | null> {
  if (state.accessToken && Date.now() < state.expiresAt - RENEW_MARGIN_MS) {
    return state.accessToken;
  }

  inFlight ??= refreshOnce().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/**
 * Retoma a sessão guardada no cookie, uma única vez por carregamento.
 *
 * O access token vive só em memória (decisão 6), então recarregar a página o
 * perde — mas o refresh está no cookie httpOnly e continua valendo. Sem esta
 * chamada, ninguém jamais batia em `/api/auth/refresh` ao abrir o app: o
 * guarda de rota lia "sem usuário" e redirecionava na hora. Na prática, o
 * operador que recarregasse o /controle no meio do jogo caía na tela de
 * login.
 */
let bootstrapInFlight: Promise<void> | null = null;

export function bootstrapSession(): Promise<void> {
  if (bootstrapped) return Promise.resolve();

  bootstrapInFlight ??= accessToken()
    .catch(() => null)
    .then(() => {
      bootstrapped = true;
      notify();
    });

  return bootstrapInFlight;
}

export async function login(input: {
  email: string;
  password: string;
  organizationId?: string;
}): Promise<SessionResponse> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });

  const body = await response.json();
  if (!response.ok) throw new Error(body?.error?.message ?? 'Não foi possível entrar.');

  applySession(body as SessionResponse);
  return body as SessionResponse;
}

export async function switchOrganization(organizationId: string): Promise<SessionResponse> {
  const token = await accessToken();
  const response = await fetch('/api/auth/switch-org', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    credentials: 'same-origin',
    body: JSON.stringify({ organizationId }),
  });

  const body = await response.json();
  if (!response.ok) throw new Error(body?.error?.message ?? 'Não foi possível trocar.');

  applySession(body as SessionResponse);
  return body as SessionResponse;
}

/** Diagnóstico: o navegador tem a Web Locks API? (false em http na LAN) */
export function usingNativeLocks(): boolean {
  return hasWebLocks();
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(
    () => undefined,
  );
  clearSession();
}
